import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ModelInfo } from '../lib/api';
import { filterModels, readFavs, sayPrice, sortModels, toggleFav, type SortKey } from '../lib/models';
import { ModelInfoPanel } from './ModelInfo';

// ---------------------------------------------------------------------------
// The model picker (204): one control, shared by Quick and Pro.
//
// A trigger that names the chosen model and its price; a popover with a
// search box, favourites, a sort and two filters, and the list — each row
// the vendor's mark, the name, its badges (Private or Anon in the vendor's
// own words, New for a fortnight, Featured, House, Yours) and the price in
// mono. A deprecated row is greyed with its replacement named, and picking
// it picks the replacement. The video bench gets the card variant, with the
// blurb, the durations and the resolutions the model offers.
//
// A popover and not a lightbox, on purpose: the quote line under the bench
// changes with the choice, and it has to stay in view while the creator is
// choosing. It behaves like a select on the keyboard — arrows, Enter,
// Escape — and closes on a click outside.
//
// 205: an ⓘ on the trigger and on every row opens the info panel — the
// blurb, every price, what the model can do — and its one button picks.
// ---------------------------------------------------------------------------

export type PickerVariant = 'row' | 'card';

export function ModelPicker({ bench, models, value, onChange, variant = 'row', label }: {
  bench: string;
  /** Null while the list is loading. */
  models: ModelInfo[] | null;
  value: ModelInfo | null;
  onChange: (m: ModelInfo) => void;
  variant?: PickerVariant;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [privacy, setPrivacy] = useState<'private' | 'anonymized' | null>(null);
  const [vendor, setVendor] = useState<string | null>(null);
  const [favsOnly, setFavsOnly] = useState(false);
  const [favs, setFavs] = useState<string[]>(() => readFavs());
  const [active, setActive] = useState(0);
  const [info, setInfo] = useState<ModelInfo | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const listId = useId();

  const vendors = useMemo(() => [...new Set((models ?? []).map((m) => m.vendor))].sort(), [models]);
  const shown = useMemo(() => sortModels(filterModels(models ?? [], { q, privacy, vendor, favsOnly, favs }), sort), [models, q, privacy, vendor, favsOnly, favs, sort]);

  useEffect(() => { if (open) { setActive(Math.max(0, shown.findIndex((m) => m.id === value?.id))); setTimeout(() => search.current?.focus(), 0); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) close(); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function close() { setOpen(false); trigger.current?.focus(); }
  function pick(m: ModelInfo) {
    // A deprecated model is still a press until its date, but choosing it means its replacement.
    const target = m.status === 'deprecated' && m.replacement ? (models ?? []).find((x) => x.id === m.replacement!.id) ?? m : m;
    onChange(target);
    close();
  }
  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(shown.length - 1, i + 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); return; }
    if (e.key === 'Enter') { e.preventDefault(); const m = shown[active]; if (m) pick(m); }
  }

  const privacyWord = (p: ModelInfo['privacy']) => (p === 'private' ? 'Private' : p === 'anonymized' ? 'Anon' : null);
  const priceLine = (m: ModelInfo) => sayPrice(m.credits, m.unit);

  return (
    <div className="picker" data-tool="model-picker" ref={wrap} onKeyDown={onKey}>
      {label && <span className="lbl">{label}</span>}
      <button type="button" className="picker-trigger" ref={trigger} role="combobox" aria-expanded={open} aria-controls={listId} aria-haspopup="listbox"
              disabled={!models} title={value?.blurb ?? (models ? 'Choose a model' : 'Reading the catalogue…')} onClick={() => setOpen((o) => !o)}>
        <span className="picker-name">{value ? value.name : models ? 'Choose a model' : 'Loading models…'}</span>
        {value && privacyWord(value.privacy) && <span className={`picker-badge ${value.privacy}`}>{privacyWord(value.privacy)}</span>}
        <span className="picker-price">{value ? priceLine(value) : '…'}</span>
        <span className="picker-caret" aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      {value && (
        <button type="button" className="picker-info" data-tool="model-info-open" aria-label={`About ${value.name}`} title="About this model"
                onClick={(e) => { e.stopPropagation(); setInfo(value); }}>ⓘ</button>
      )}
      {info && <ModelInfoPanel bench={bench} model={info} onClose={() => setInfo(null)} onUse={(m) => { pick(m); }} />}
      {open && models && (
        <div className="picker-pop">
          <input ref={search} className="picker-search" data-tool="model-search" type="search" placeholder="Search models…" value={q}
                 aria-controls={listId} aria-activedescendant={shown[active] ? `${listId}-${shown[active].id}` : undefined}
                 onChange={(e) => { setQ(e.target.value); setActive(0); }} />
          <div className="picker-bar">
            <button type="button" className={`chip${favsOnly ? ' on' : ''}`} title="Only your favourites" onClick={() => setFavsOnly((f) => !f)}>★ {favs.length}</button>
            <select value={sort} aria-label="Sort" onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="name">Name</option><option value="price">Price</option><option value="new">New first</option>
            </select>
            <select value={privacy ?? ''} aria-label="Privacy" onChange={(e) => setPrivacy((e.target.value || null) as 'private' | 'anonymized' | null)}>
              <option value="">Any privacy</option><option value="private">Private</option><option value="anonymized">Anon</option>
            </select>
            {vendors.length > 1 && (
              <select value={vendor ?? ''} aria-label="Vendor" onChange={(e) => setVendor(e.target.value || null)}>
                <option value="">Any vendor</option>{vendors.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
          </div>
          <ul className="picker-list" role="listbox" id={listId} aria-label={`${bench} models`}>
            {shown.length === 0 && <li className="picker-empty">Nothing matches.</li>}
            {shown.map((m, i) => {
              const gone = m.status === 'deprecated';
              return (
                <li key={m.id} id={`${listId}-${m.id}`} role="option" aria-selected={value?.id === m.id} aria-disabled={gone || undefined}
                    className={`picker-row${variant === 'card' ? ' picker-card' : ''}${value?.id === m.id ? ' on' : ''}${i === active ? ' active' : ''}${gone ? ' gone' : ''}`}
                    onMouseEnter={() => setActive(i)} onClick={() => pick(m)}>
                  <span className="picker-mark" aria-hidden="true">{m.vendor.slice(0, 1).toUpperCase()}</span>
                  <span className="picker-main">
                    <span className="picker-row-name">{m.name}</span>
                    <span className="picker-badges">
                      {privacyWord(m.privacy) && <span className={`picker-badge ${m.privacy}`}>{privacyWord(m.privacy)}</span>}
                      {m.isNew && <span className="picker-badge new">New</span>}
                      {m.featured && <span className="picker-badge">Featured</span>}
                      {m.houseDefault && <span className="picker-badge">House</span>}
                      {m.workspaceDefault && <span className="picker-badge">Yours</span>}
                      {gone && <span className="picker-badge gone">{m.replacement ? `→ ${m.replacement.name}` : 'Going away'}</span>}
                    </span>
                    {variant === 'card' && m.blurb && <span className="picker-blurb">{m.blurb}</span>}
                    {variant === 'card' && (m.constraints.durations || m.constraints.resolutions) && (
                      <span className="picker-meta">
                        {m.constraints.durations ? `${m.constraints.durations.join('/')} s` : ''}
                        {m.constraints.durations && m.constraints.resolutions ? ' · ' : ''}
                        {m.constraints.resolutions ? m.constraints.resolutions.join(', ') : ''}
                        {m.constraints.audio === false ? ' · silent' : ''}
                      </span>
                    )}
                  </span>
                  <span className="picker-price">{priceLine(m)}</span>
                  <button type="button" className="picker-info" aria-label={`About ${m.name}`} title="About this model"
                          onClick={(e) => { e.stopPropagation(); setInfo(m); }}>ⓘ</button>
                  <button type="button" className={`picker-fav${favs.includes(m.id) ? ' on' : ''}`} aria-label={favs.includes(m.id) ? 'Unfavourite' : 'Favourite'} title="Favourite"
                          onClick={(e) => { e.stopPropagation(); setFavs(toggleFav(m.id)); }}>★</button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
